/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { createContext, useContext, useEffect, useState } from 'react';
import type ResourceItem from 'interfaces/resource';
import { Permission, Token, TokenContextType } from 'contexts/resource/type';
import fetchPermissions from 'core/utils/fetchPermissions';
import { useSetLocale } from '@refinedev/core';
import fetchOrganization from 'core/utils/fetchOrg';
// eslint-disable-next-line import/no-extraneous-dependencies
import io, { Socket } from 'socket.io-client';

import fetchNotificatons from 'core/utils/fetchNotifications';
import fetchPrograms from 'core/utils/fetchPrograms';
import axiosInstance from 'providers/axiosInstance/axiosInstance';
import { REACT_APP_SOCKET_URL } from 'config';
// import { H } from 'highlight.run';

const OrgIDKey = 'orgId';
const programIdKey = 'programId';

const TokenContext = createContext<TokenContextType>({
  token: undefined,
  setToken: () => {},
  userSessionId: undefined,
  setUserSessionId: () => {},
  crudPermissions: [],
  setCrudPermission: () => {},
  modulePermission: [],
  setModulePermission: () => {},
  userDetails: {},
  setUserDetails: () => {},
  filteredResources: [],
  setFilteredResources: () => {},
  loading: false,
  setLoading: () => {},
  orgId: '',
  setOrgId: () => {},
  programId: '',
  setProgramId: () => {},
  userProgramList: [],
  organizationList: [],
  setOrganizationDropdownList: () => {},
  currentLang: '',
  setCurrentLang: () => {},
  setRoles: () => {},
  roles: [],
  socketInstance: undefined,
  setSocketInstance: () => {},
  setNotificationMessages: () => {},
  notificationMessages: [],
  paymentPartyName: '',
  setPaymentPartyName: () => {},
  compensationType: '',
  userType: '',
  setUserType: () => {},
  setCompensationType: () => {},
});

export function useTokenContext() {
  return useContext(TokenContext);
}

const TokenProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<Token>(undefined);
  const [userSessionId, setUserSessionId] = useState<string>(undefined);
  const [crudPermissions, setCrudPermission] = useState<Permission>([]);
  const [modulePermission, setModulePermission] = useState<Permission>([]);
  const [filteredResources, setFilteredResources] = useState<ResourceItem[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState<any>({});
  const [roles, setRoles] = useState([]);

  const [orgId, setOrgIdState] = useState<string | number>(() => {
    const storedOrgId = localStorage.getItem(OrgIDKey);
    return storedOrgId || '';
  });
  const [programId, setProgramIdState] = useState<string>(() => {
    const storedProgramId = localStorage.getItem(programIdKey);
    return storedProgramId || '';
  });
  const [paymentPartyName, setPaymentPartyName] = useState();
  const [compensationType, setCompensationType] = useState('');
  const [organizationList, setOrganizationList] = useState<any[]>([]);
  const [userProgramList, setUserProgramList] = useState<any[]>([]);
  const [currentLang, setCurrentLang] = useState('en');
  const changeLanguage = useSetLocale();
  const [socketInstance, setSocketInstance] = useState<Socket | undefined>(
    undefined
  );

  const [notificationMessages, setNotificationMessages] = useState<any>(() => {
    const notificationList = localStorage.getItem('notifications');
    return notificationList || [];
  });
  const [userType, setUserType] = useState('');
  // Function to set orgId in both state and localStorage
  const setOrgId = (newOrgId: string) => {
    setOrgIdState(newOrgId);
  };

  // Function to set programId in both state and localStorage
  const setProgramId = (newProgramId: any) => {
    setProgramIdState(newProgramId);
  };

  const connect = (id) => {
    const userId = id;
    if (socketInstance) {
      return;
    }

    if (userId && orgId) {
      const newSocket = io(REACT_APP_SOCKET_URL, {
        query: { userId, orgId },
      });
      newSocket.on('connect', () => {});

      newSocket.emit('joinRoom', { roomId: `${userId}_${orgId}` });

      // Set the new socket instance
      setSocketInstance(newSocket);
    }
  };

  useEffect(() => {
    if (socketInstance && userDetails) {
      fetchNotificatons(
        setNotificationMessages,
        userDetails,
        socketInstance,
        notificationMessages
      );
    }
  }, [socketInstance]);

  const fetchData = async () => {
    setLoading(true);
    axiosInstance.defaults.headers.common = {
      Authorization: `Bearer ${token}`,
      'user-session-id': userSessionId,
    };
    await fetchOrganization(setOrganizationList);
    await fetchPermissions(
      setFilteredResources,
      setCrudPermission,
      setModulePermission,
      setUserDetails,
      changeLanguage,
      setCurrentLang,
      connect,
      currentLang,
      token,
      setOrgId,
      setProgramId,
      userSessionId
    );
    await fetchPrograms(setUserProgramList);
    setLoading(false);
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  useEffect(() => {
    if (!!programId && userProgramList?.length > 0) {
      const program = userProgramList?.find(
        (item) => item.iprogram_unique_id === programId
      );
      setPaymentPartyName(program?.payment_party_name);
      setCompensationType(program?.compensation_type);
      setUserType(program?.user_type);
    }
  }, [programId, userProgramList]);

  // H.init('56gl9g91', {
  //   serviceName: 'data-management',
  //   environment: 'production',
  //   disableConsoleRecording: false,
  //   version: '0.1',
  //   tracingOrigins: true,
  //   networkRecording: { enabled: true, recordHeadersAndBody: true },
  //   backendUrl: 'https://highlight.augment8.dev/public ',
  // });

  // H.identify(`${userDetails.user_mail_id}`, {
  //   id: userDetails?.unique_id,
  //   orgId: userDetails?.role_id,
  //   roleId: userDetails?.organization_id,
  //   email: userDetails?.email,
  //   name: userDetails?.name,
  //   role: userDetails?.role_name,
  // });

  return (
    <TokenContext.Provider
      value={{
        token,
        setToken,
        crudPermissions,
        setCrudPermission,
        modulePermission,
        setModulePermission,
        filteredResources,
        setFilteredResources,
        userDetails,
        setUserDetails,
        loading,
        setLoading,
        orgId,
        setOrgId,
        setProgramId,
        programId,
        userProgramList,
        organizationList,
        setOrganizationDropdownList: setOrganizationList,
        currentLang,
        setCurrentLang,
        roles,
        setRoles,
        socketInstance,
        setSocketInstance,
        notificationMessages,
        setNotificationMessages,
        userSessionId,
        setUserSessionId,
        paymentPartyName,
        setPaymentPartyName,
        compensationType,
        setCompensationType,
        userType,
        setUserType,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export default TokenProvider;
